import React from 'react';
import { Outlet, useLocation, useParams } from '@tanstack/react-router';
import {
  SegmentedSelection,
  HelpLink,
  Layout,
  Stack,
  withTranslation,
} from '@piwikpro/ui-components';
import { ListPreference } from '@piwikpro/user-preferences';
import { TFunction } from 'react-i18next';
import {
  withInject,
  view,
  withAccess,
  errorBoundary,
} from '@piwikpro/platform';
import { RouterService } from '@piwikpro/router-crate';

interface InjectedProps {
  accessManager: any
  userPreferences: any
  routerService: RouterService
}

interface Props extends InjectedProps {
  t: TFunction
}

const Permissions: React.FC<Props> = ({
  accessManager,
  userPreferences,
  routerService,
  t,
}) => {
  const { pathname } = useLocation();
  const params = useParams({ from: '/_secured/administration/meta-sites/$metaSiteId' });
  const listPreferences = new ListPreference(userPreferences.load(), '');

  const permissionsSection = pathname.includes('/permissions/users') || /\/permissions\/?$/.test(pathname)
    ? 'users'
    : 'userGroups';

  const selectionButtons = [
    {
      name: t('meta-sites.details.permissions.users.button'),
      value: 'users',
    },
    {
      name: t('meta-sites.details.permissions.user-groups.button'),
      value: 'userGroups',
    },
  ];

  const handlePermissionsViewChange = (newValue: string) => {
    if (!newValue) {
      return;
    }

    if (newValue === selectionButtons[0].value) {
      routerService.push({
        to: '/administration/meta-sites/$metaSiteId/permissions/users/$page/$limit',
        params: {
          metaSiteId: params.metaSiteId,
          page: '1',
          limit: String(listPreferences.limit),
        },
      });
    } else if (newValue === selectionButtons[1].value) {
      routerService.push({
        to: '/administration/meta-sites/$metaSiteId/permissions/user-groups/$page/$limit',
        params: {
          metaSiteId: params.metaSiteId,
          page: '1',
          limit: String(listPreferences.limit),
        },
      });
    }
  };

  return (
    <>
      <Layout>
        <Stack vertical spacing="wide">
          <Stack.Item fill>
            {accessManager.isGranted({ role: 'OWNER' }) && (
              <SegmentedSelection
                value={permissionsSection}
                fullWidth
                items={selectionButtons}
                onChange={handlePermissionsViewChange}
              />
            )}
          </Stack.Item>
          <Stack.Item fill>
            <Stack vertical horizontalAlignment="middle">
              <Outlet />
              <Stack.Item>
                <HelpLink href="https://help.piwik.pro/support/getting-started/permissions-for-meta-sites/">
                  Permissions for meta sites
                </HelpLink>
              </Stack.Item>
            </Stack>
          </Stack.Item>
        </Stack>
      </Layout>
    </>
  );
};

export default withInject<InjectedProps>({
  userPreferences: 'userPreferences',
  routerService: 'RouterCrate.router',
})(
  view({ role: 'USER', level: 'manage' })(withAccess(withTranslation(['administration'])(
    errorBoundary(Permissions),
  ))),
);
